// The analysis queue as the admin panel reads it (docs/GAME-DIAGNOSIS.md). The worker takes one job at a
// time, oldest first, and writes a heartbeat while it works; a running job whose heartbeat is older than
// STALE_MINUTES has a dead worker behind it and goes back to the queue. Pure: instants as the server sends
// them, every age measured against the server's own `now`, because the browser's clock may not agree with it.

export interface QueueJob {
    id: number;
    userId: number;
    email: string;
    kind: string;
    status: string;
    depth: number;
    gamesTotal: number | null;
    gamesDone: number;
    positionsTotal: number | null;
    positionsDone: number;
    error: string | null;
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
    heartbeatAt: string | null;
}

export interface OrphanReservation {
    userId: number;
    email: string;
    reserved: number;
    updatedAt: string;
}

export interface QueueView {
    now: string;
    active: QueueJob[];
    activeTotal: number;
    failed: QueueJob[];
    finished: QueueJob[];
    orphans: OrphanReservation[];
}

/** The worker's own numbers (worker/diagnosis/worker.mjs): its heartbeat threshold and how often it polls. */
export const STALE_MINUTES = 3;
/** Nothing running and the head of the queue older than this: the worker is not picking work up. */
const PICKUP_GRACE_S = 60;
/** A rate over less than this says more about rounding than about the engine. */
const PACE_MIN_ELAPSED_S = 30;

/** Whole seconds from one instant to another; null when the first was never written. */
export function seconds(from: string | null | undefined, to: string): number | null {
    if (!from) return null;
    const gap = (Date.parse(to) - Date.parse(from)) / 1000;
    return Number.isNaN(gap) ? null : Math.max(0, Math.round(gap));
}

export type JobState = "running" | "stalled" | "requeued" | "waiting";

/** What one active row is going through. `stalled` and `requeued` are two faces of a worker dying on it. */
export function jobState(job: QueueJob, now: string): JobState {
    if (job.status === "running") {
        const beat = seconds(job.heartbeatAt, now);
        return beat !== null && beat > STALE_MINUTES * 60 ? "stalled" : "running";
    }
    return job.startedAt ? "requeued" : "waiting";
}

export type WorkerState =
    | { kind: "idle" }
    | { kind: "working"; job: QueueJob; beatAgoS: number }
    | { kind: "stalled"; job: QueueJob; beatAgoS: number }
    | { kind: "picking"; waitingS: number }
    | { kind: "down"; waitingS: number };

/**
 * What the queue says about the worker — the first question the tab answers. One job runs at a time, so one
 * running row with a fresh heartbeat is a worker at work and everything queued is simply behind it; queued
 * work that nobody picks up within the grace is a worker that is not there.
 */
export function workerState(active: QueueJob[], now: string): WorkerState {
    const running = active.find((job) => job.status === "running");
    if (running) {
        const beatAgoS = seconds(running.heartbeatAt, now) ?? 0;
        return beatAgoS > STALE_MINUTES * 60
            ? { kind: "stalled", job: running, beatAgoS }
            : { kind: "working", job: running, beatAgoS };
    }
    const head = active[0];
    if (!head) return { kind: "idle" };
    const waitingS = seconds(head.createdAt, now) ?? 0;
    return waitingS > PICKUP_GRACE_S ? { kind: "down", waitingS } : { kind: "picking", waitingS };
}

export interface Pace {
    /** Positions the engine settles a minute, counted since the job started. */
    perMinute: number;
    /** Seconds until the positions run out at that rate; null while the total is unknown. */
    etaSeconds: number | null;
}

/** What a pace is read from: any job that says when it started and how many positions it has settled. */
export interface PaceInput {
    startedAt?: string | null;
    positionsDone: number;
    positionsTotal: number | null;
}

/**
 * How fast a running job is really going, from its own progress — which is the answer to "is it slow or is it
 * stuck". `startedAt` survives a requeue, so after a stall the rate reads lower than the engine's true one.
 */
export function pace(job: PaceInput, now: string): Pace | null {
    const elapsed = seconds(job.startedAt ?? null, now);
    if (elapsed === null || elapsed < PACE_MIN_ELAPSED_S || job.positionsDone <= 0) return null;
    const perMinute = (job.positionsDone / elapsed) * 60;
    const left = job.positionsTotal === null ? null : Math.max(0, job.positionsTotal - job.positionsDone);
    return { perMinute, etaSeconds: left === null ? null : Math.round((left / perMinute) * 60) };
}
